import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import { createResearchKpiGroupValidationSchema } from 'features/ResearchKpiGroup/validation';
import { NumberInput, TextInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(createResearchKpiGroupValidationSchema) };

const ResearchKpiGroupForm = forwardRef(({ isLoading, refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [researchKpiGroupControl, setResearchKpiGroupControl] = useState(null);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors, isDirty, dirtyFields },
  } = useForm({ ...formOptions, mode: 'onChange' });
  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_researchKpiGroupControl) => {
        setResearchKpiGroupControl(_researchKpiGroupControl);
        setVisible(true);
        const { id, name, cost, appliedYear } = _researchKpiGroupControl.value;

        if (_researchKpiGroupControl.mode === 'create') {
          reset({ appliedYear });
        } else if (_researchKpiGroupControl.mode === 'update') {
          reset({ id, name, cost });
        }
      },
    }),
    []
  );

  const onSubmit = (data) => {
    if (researchKpiGroupControl.mode === 'create') {
      const newData = { ...data };

      researchKpiGroupControl.mutate(newData, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('researchKpiGroup.create.success'));
          reset();
          refetch();
          setVisible(false);
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    } else {
      const { id, ...newData } = { ...data };

      researchKpiGroupControl.mutate(
        { id, data: newData },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('researchKpiGroup.update.success'));
            refetch();
            setVisible(false);
          },
          onError: (error) => {
            toast(TOAST.ERROR, mapError(t, error));
          },
        }
      );
    }
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion

  return (
    <Dialog
      header={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <p className="text-blue-500">
          {researchKpiGroupControl?.mode === 'create'
            ? t('researchKpiGroup.create.title')
            : t('researchKpiGroup.update.title')}
        </p>
      }
      visible={visible}
      style={{ maxWidth: '500px' }}
      onHide={() => {
        reset({});
        setVisible(false);
      }}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={checkKeyDown}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid">
            <div className="col-12">
              <TextInput
                label={t('researchKpiGroup.name')}
                name="name"
                control={control}
                errors={errors}
                isRequired
              />
            </div>
            <div className="col-12 mb-2">
              <NumberInput
                label={t('researchKpiGroup.cost')}
                name="cost"
                control={control}
                errors={errors}
                min={0}
                isRequired
              />
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-6">
              <Button
                label={t('researchKpiGroup.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset({});
                  setVisible(false);
                }}
              />
            </div>
            <div className="col-6">
              <Button
                label={
                  researchKpiGroupControl?.mode === 'CREATE'
                    ? t('researchKpiGroup.add')
                    : t('researchKpiGroup.save')
                }
                icon="pi pi-pencil"
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                onClick={checkFormValid}
                disabled={!(isDirty && Object.keys(dirtyFields).length)}
              />
            </div>
          </div>
        </form>
        {isLoading && <Loading />}
      </div>
    </Dialog>
  );
});

export default ResearchKpiGroupForm;

ResearchKpiGroupForm.propTypes = {
  refetch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
ResearchKpiGroupForm.defaultProps = {
  isLoading: false,
};
